/**
 * 🧪 EXECUTIVE DASHBOARD MOCK DATA
 * ════════════════════════════════════════════════════════════════════════
 * 
 * Data de ejemplo para testing del sistema ejecutivo.
 * Simula respuesta del backend con ExecutivePresentationEngine.
 */

import type { ExecutiveDashboard } from './types';

export const mockExecutiveTimeTracking: ExecutiveDashboard = {
  id: 'exec-tt-001',
  title: 'Control de Horas - Diciembre 2024',
  description: 'Dashboard ejecutivo de seguimiento de horas trabajadas',
  
  presentation: {
    mode: 'EXECUTIVE',
    decisions: [
      'PRIMARY_INSIGHT forced at position 0',
      'DRIVERS positioned at rows 1-2',
      'TREND positioned at row 3',
      'DETAIL positioned at row 4',
      'Intent-aware ordering: time_tracking'
    ]
  },
  
  banners: [
    {
      id: 'b1',
      type: 'insight',
      message: '85% de utilización - Target: 80%. Equipo está en zona óptima.',
      priority: 1
    }
  ],
  
  widgets: [
    // 1️⃣ PRIMARY_INSIGHT (hero metric)
    {
      id: 'w-primary',
      type: 'metric',
      role: 'PRIMARY_INSIGHT',
      title: 'Horas Facturables Totales',
      data: [{ valor: 2847 }],
      layout: { x: 0, y: 0, w: 12, h: 1 },
      metadata: { format: 'number', decimals: 0 }
    },
    
    // 2️⃣ DRIVER - Por proyecto
    {
      id: 'w-driver-1',
      type: 'bar',
      role: 'DRIVER',
      title: 'Horas por Proyecto',
      data: [
        { proyecto: 'Proyecto A', valor: 856 },
        { proyecto: 'Proyecto B', valor: 723 },
        { proyecto: 'Proyecto C', valor: 612 },
        { proyecto: 'Proyecto D', valor: 456 },
        { proyecto: 'Proyecto E', valor: 200 }
      ],
      layout: { x: 0, y: 1, w: 6, h: 1 },
      metadata: {}
    },
    
    // 3️⃣ DRIVER - Por equipo
    {
      id: 'w-driver-2',
      type: 'bar',
      role: 'DRIVER',
      title: 'Horas por Equipo',
      data: [
        { equipo: 'Desarrollo', valor: 1234 },
        { equipo: 'Diseño', valor: 678 },
        { equipo: 'QA', valor: 456 },
        { equipo: 'DevOps', valor: 345 },
        { equipo: 'PM', valor: 134 }
      ],
      layout: { x: 6, y: 1, w: 6, h: 1 },
      metadata: {}
    },
    
    // 4️⃣ TREND
    {
      id: 'w-trend',
      type: 'line',
      role: 'TREND',
      title: 'Evolución Últimos 30 Días',
      data: [
        { fecha: '01/12', valor: 89 },
        { fecha: '02/12', valor: 95 },
        { fecha: '03/12', valor: 92 },
        { fecha: '04/12', valor: 88 },
        { fecha: '05/12', valor: 101 },
        { fecha: '06/12', valor: 97 },
        { fecha: '09/12', valor: 103 },
        { fecha: '10/12', valor: 99 },
        { fecha: '11/12', valor: 96 },
        { fecha: '12/12', valor: 104 },
        { fecha: '13/12', valor: 98 },
        { fecha: '16/12', valor: 102 },
        { fecha: '17/12', valor: 97 },
        { fecha: '18/12', valor: 95 },
        { fecha: '19/12', valor: 100 },
        { fecha: '20/12', valor: 93 }
      ],
      layout: { x: 0, y: 2, w: 12, h: 1 },
      metadata: {}
    },
    
    // 5️⃣ DETAIL
    {
      id: 'w-detail',
      type: 'table',
      role: 'DETAIL',
      title: 'Detalle por Empleado (Top 10)',
      data: [
        { empleado: 'Juan Pérez', proyecto: 'Proyecto A', horas: 168, facturables: 152 },
        { empleado: 'María García', proyecto: 'Proyecto B', horas: 165, facturables: 148 },
        { empleado: 'Carlos López', proyecto: 'Proyecto A', horas: 172, facturables: 156 },
        { empleado: 'Ana Martínez', proyecto: 'Proyecto C', horas: 160, facturables: 144 },
        { empleado: 'Luis Rodríguez', proyecto: 'Proyecto D', horas: 158, facturables: 142 },
        { empleado: 'Laura Sánchez', proyecto: 'Proyecto B', horas: 163, facturables: 147 },
        { empleado: 'Pedro Gómez', proyecto: 'Proyecto E', horas: 155, facturables: 140 },
        { empleado: 'Sofía Torres', proyecto: 'Proyecto A', horas: 170, facturables: 154 },
        { empleado: 'Diego Ruiz', proyecto: 'Proyecto C', horas: 162, facturables: 146 },
        { empleado: 'Carmen Díaz', proyecto: 'Proyecto D', horas: 159, facturables: 143 }
      ],
      layout: { x: 0, y: 3, w: 12, h: 1 },
      metadata: {}
    }
  ],
  
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

export const mockExecutiveSales: ExecutiveDashboard = {
  id: 'exec-sales-001',
  title: 'Ventas Q4 2024',
  description: 'Dashboard ejecutivo de ventas del último trimestre',
  
  presentation: {
    mode: 'EXECUTIVE',
    decisions: [
      'PRIMARY_INSIGHT forced at position 0',
      'Intent-aware ordering: ventas',
      'Visual emphasis applied'
    ]
  },
  
  widgets: [
    // PRIMARY
    {
      id: 'w-primary-sales',
      type: 'metric',
      role: 'PRIMARY_INSIGHT',
      title: 'Revenue Total Q4',
      data: [{ valor: 1250000 }],
      layout: { x: 0, y: 0, w: 12, h: 1 },
      metadata: { format: 'currency', decimals: 0 }
    },
    
    // DRIVERS
    {
      id: 'w-driver-region',
      type: 'bar',
      role: 'DRIVER',
      title: 'Ventas por Región',
      data: [
        { region: 'CABA', valor: 450000 },
        { region: 'GBA', valor: 380000 },
        { region: 'Interior', valor: 290000 },
        { region: 'Exterior', valor: 130000 }
      ],
      layout: { x: 0, y: 1, w: 6, h: 1 },
      metadata: {}
    },
    
    {
      id: 'w-driver-product',
      type: 'bar',
      role: 'DRIVER',
      title: 'Ventas por Producto',
      data: [
        { producto: 'Producto A', valor: 520000 },
        { producto: 'Producto B', valor: 380000 },
        { producto: 'Producto C', valor: 230000 },
        { producto: 'Producto D', valor: 120000 }
      ],
      layout: { x: 6, y: 1, w: 6, h: 1 },
      metadata: {}
    },
    
    // TREND
    {
      id: 'w-trend-monthly',
      type: 'line',
      role: 'TREND',
      title: 'Evolución Mensual',
      data: [
        { mes: 'Octubre', valor: 385000 },
        { mes: 'Noviembre', valor: 425000 },
        { mes: 'Diciembre', valor: 440000 }
      ],
      layout: { x: 0, y: 2, w: 12, h: 1 },
      metadata: {}
    }
  ],
  
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};
